import { Component, HostListener, isDevMode, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { NavigationEnd, NavigationStart, NavigationError, NavigationCancel, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  NgcCookieConsentService,
  NgcInitializeEvent,
  NgcNoCookieLawEvent,
  NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { Subject, Subscription } from 'rxjs';
import { CommonService } from './core/services/common.service';
import { LoadingService } from './core/services/loading.service';
import { RouteService } from './core/services/route.service';
import { StorageService } from './core/services/storage.service';
import { debounceTime } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { LaunchDarklyService } from './core/services/launchdarkly.service';
import { FEATURE_FLAGS } from './shared/launchdarkly.constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'ignatius';
  isFileView: boolean = true;
  private isFileViewPending: boolean | null = null;
  isChatBot: boolean = false;
  progressProps = {
    medium: { diameter: 60, strokeWidth: 6 },
  }
  showResolverLoader: boolean = false;
  isAuthAccessibilityEnabled: boolean = false;

  private popupOpenSubscription: Subscription = new Subscription();
  private popupCloseSubscription: Subscription = new Subscription();
  private initializeSubscription: Subscription = new Subscription();
  private statusChangeSubscription: Subscription = new Subscription();
  private revokeChoiceSubscription: Subscription = new Subscription();
  private noCookieLawSubscription: Subscription = new Subscription();
  private readonly RESIZE_DEBOUNCE_TIME = 250; // ms
  private resizeSubject = new Subject<Event>();
  subsink: SubSink = new SubSink();

  constructor(
    public loadingService: LoadingService,
    public router: Router,
    private routeService: RouteService,
    private modal: NgbModal,
    private ccService: NgcCookieConsentService,
    private storage:StorageService,
    private readonly commonService:CommonService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {
    document.body.className = 'opengov-theme';
    requestAnimationFrame(() => {
      this.onGoogleTranslateInitialized();
    });
    this.subsink.add(
    this.resizeSubject.pipe(debounceTime(this.RESIZE_DEBOUNCE_TIME)).subscribe(() => {this.OnScreenChanges();}));
  }

    // Listen to window resize events
    @HostListener('window:resize', ['$event'])
    onResize(event: Event): void {
      this.resizeSubject.next(event);
    }

  OnScreenChanges(): void {
    const isSmall = window.innerWidth <= 767;
    this.commonService.setIsSmallScreen(isSmall);
  }

  get isIgnatiusThemeApplied(): boolean {
    return this.commonService.isIgnatiusThemeApplied;
  }

  ngOnInit(): void {

    this.OnScreenChanges()
    this.getNewRelicScript();
    this.subsink.add(
      this.router.events.subscribe((event) => {
        this.modal.dismissAll();
        if (event instanceof NavigationStart) {
          this.routeService.getApplicationName();
          // Stage the bound flag and apply after current CD to avoid NG0100
          this.isFileViewPending = event.url.includes('file-view');
          setTimeout(() => {
            if (this.isFileViewPending !== null) {
              this.isFileView = this.isFileViewPending;
              this.isFileViewPending = null;
            }
          });
          // Show resolver loader for auth routes with appId
          // Check if URL matches auth routes with appId parameter
          const url = event.url.split('?')[0]; // Remove query params
          const authRoutePatterns = [
            /^\/auth\/login\/[^/]+/,
            /^\/auth\/register\/[^/]+/,
            /^\/auth\/resetpassword\/[^/]+/,
            /^\/auth\/resendmfacode\/[^/]+/
          ];
          const hasResolver = authRoutePatterns.some(pattern => pattern.test(url));
          if (hasResolver) {
            this.showResolverLoader = true;
          }
        }
        if(event instanceof NavigationEnd){
          // Defer state-bound updates to avoid NG0100 (values changing during check)
          setTimeout(() => {
            this.routeService.getApplicationName();
          });
          this.showResolverLoader = false;
          // Clear loading state when navigating to /apps or auth routes to ensure loader doesn't persist after Auth0 callback or logout
          if (event.url.startsWith('/apps') || event.url.startsWith('/auth/')) {
            setTimeout(() => {
              this.loadingService.loadingState = false;
              this.loadingService.tableLoadingState = false;
            }, 100);
          }
        }
        if(event instanceof NavigationError || event instanceof NavigationCancel){
          this.showResolverLoader = false;
        }
      }));

    // subscribe to cookieconsent observables to react to main events
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.initializeSubscription = this.ccService.initialize$.subscribe(
      (event: NgcInitializeEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
      (event: NgcStatusChangeEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(
      () => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe(
      (event: NgcNoCookieLawEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    this.subsink.add(
      this.launchDarklyService.getFlagValue(FEATURE_FLAGS.ENABLE_AUTH_ACCESSIBILITY, false)
        .subscribe((value) => {
          this.isAuthAccessibilityEnabled = !!value;
          if (this.isAuthAccessibilityEnabled) {
            document.body.classList.add('aa-compliant');
          } else {
            document.body.classList.remove('aa-compliant');
          }
        })
    );
  }

  ngAfterViewInit(): void {
    // Initialize bound flag after first render to avoid NG0100 during initial navigation
    Promise.resolve().then(() => {
      this.isFileView = this.router.url.includes('file-view');
    });
  }

  getNewRelicScript() {

    if(window.location.host.includes('localhost')) return;

    if (isDevMode()) {
      let script = document.createElement('script');
      script.src = './assets/Scripts/newRelic_dev.js';
      document.head.appendChild(script);
    } else {
      let script = document.createElement('script');
      script.src = './assets/Scripts/newRelic_prod.js';
      document.head.appendChild(script);
    }
  }

  onGoogleTranslateInitialized() {
    const currentLang = document.documentElement.lang;
    if(currentLang === 'en' || currentLang === 'ab' || currentLang === 'auto' || this.storage.getAppLanguage() === 'en'){
      document.body.classList.add('notranslate');
    } else {
      document.body.classList.remove('notranslate');
    }
    if((this.storage.getAppLanguage() || 'en') !== currentLang){
      this.commonService.setGoogleTranslateLanguage(this.storage.getAppLanguage() || 'en')
    }
    requestAnimationFrame(() => {
      this.onGoogleTranslateInitialized();
    });
  }

  openOGSupport(){
    window.open('https://support.opengov.com/', '_blank', 'noopener');
  }

  openOGSupportLink(){
    return 'https://support.opengov.com/';
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
    this.popupOpenSubscription.unsubscribe();
    this.popupCloseSubscription.unsubscribe();
    this.initializeSubscription.unsubscribe();
    this.statusChangeSubscription.unsubscribe();
    this.revokeChoiceSubscription.unsubscribe();
    this.noCookieLawSubscription.unsubscribe();
    this.launchDarklyService.close();
  }
}
